import React, { FC } from 'react'
import { FilterName } from '../../__generated__/fed-graphql-types'
import { getFacetByFilter } from './facet-filter.map'
import { useFacet } from './facet.context.hoc'
import { useForm } from './form.context'

import { Filter, FilterComponentProp, ValueChangedProp, ValuesChangedTuple } from './types'

import { FilterWithSelectedOptions } from '../../utils/uri.utilities'
import { SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet_filters_Filter_options_Option } from '../../contexts/search-results-context/query/__generated__/search-results-facets.fed.query'

export type FilterContextProps = Filter & ValueChangedProp

export const defaultFilterValue = {
    options: [],
    onValueChanged: () => {},
    update: () => {},
    hideLabel: false,
    hideCount: false,
    isDisabled: false,
    placeholder: 'Any',
} as unknown as Required<FilterContextProps>

export const FilterContext = React.createContext<Required<FilterContextProps>>(defaultFilterValue)

export function getDefaultPlaceholder(filterName: FilterName) {
    switch (filterName) {
        case FilterName.deposit:
        case FilterName.yearly_mileage:
            return null
        default:
            return 'Any'
    }
}
const withFilterContext = <P extends object>(
    filterName: FilterName,
    FilterComponent: FC<P>,
    staticProps: Partial<FilterComponentProp> = {}
) => {
    const facetName = getFacetByFilter(filterName)

    const WithFilterContext = (
        props: P &
            FilterComponentProp & {
                options?: SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet_filters_Filter_options_Option[]
            }
    ) => {
        const { facet, updateSelected, isDisabled } = useFacet()
        const { selectedMap } = useForm()

        const allProps = { ...staticProps, ...props }
        const { filters = [] } = facet
        const filter = filters.find((f) => f.filter === filterName)

        const hybridOptions = filter?.sections.find((section) => section.label === 'hybridOptions')?.values || []
        const options = allProps.options || filter?.options || []

        const selectedOptions = (filter && options.filter((o) => filter.selected?.includes(o.value))) || []

        const selected = options.length ? selectedOptions.map((o) => o.value) : filter?.selected || []

        const isDisabledByForm = allProps.disabled
        const isFilterDisabled = isDisabled || !options.length || isDisabledByForm || filter?.isOnlySelected

        const {
            title = filterName,
            placeholder,
            hideLabel,
            hideCount,
            onValueChanged = () => {},
            defaultSelected = false,
            ariaLabel,
            subTitle,
        } = allProps

        const overridePlaceholder =
            isFilterDisabled && !isDisabledByForm
                ? selectedMap.get(filterName)?.labels.join(', ')
                : placeholder || getDefaultPlaceholder(filterName)

        const value = {
            ...defaultFilterValue,
            options,
            selected,
            selectedOptions,
            hybridOptions,
            filterName,
            facetName,
            hideLabel,
            hideCount,
            title,
            subTitle,
            ariaLabel,
            placeholder: overridePlaceholder,
            isDisabled: isFilterDisabled,
            defaultSelected,
            onValueChanged: (
                values: string[],
                otherFilterChanges: FilterWithSelectedOptions[],
                currentFilter: FilterName
            ) => {
                setTimeout(() => {
                    // Ensure url updates happen after UI state updates
                    const result = onValueChanged(values, otherFilterChanges, currentFilter)
                    if (result === false) return

                    const [actualValues, otherFilters = otherFilterChanges] = (result || [values]) as ValuesChangedTuple
                    updateSelected([
                        { filter: currentFilter ? currentFilter : filterName, selected: actualValues },
                        ...(otherFilters || []),
                    ])
                })
            },
        } as Required<FilterContextProps>

        return (
            <FilterContext.Provider value={value as unknown as Required<FilterContextProps>}>
                <FilterComponent {...staticProps} {...props} />
            </FilterContext.Provider>
        )
    }

    WithFilterContext.displayName = `${FilterComponent.displayName}Filter`
    return WithFilterContext
}

const useFilter = () => React.useContext(FilterContext)

export { withFilterContext, useFilter }
